import React, { useState, useEffect } from 'react'
import {Collapse, Alert, Card, Form, Button, InputGroup, Modal} from 'react-bootstrap'
import { get_json, post_json, delete_json } from './util.js'

import * as form from './form-elements.js'

const ConfirmationModal = ({ show, title, message, onConfirm, onCancel }) => (
  <Modal show={show} onHide={onCancel} centered>
    <Modal.Header className="bg-danger text-white">
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body>{message}</Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="danger" onClick={onConfirm}>
        Delete
      </Button>
    </Modal.Footer>
  </Modal>
);

class AddSourceView extends React.Component {
  constructor(props) {
      super(props);
      this.state = {
          data: {
              name: "",
              url: "",
              refresh: "10 min",
              enabled: true,
              fetch_linked_files: false
          },
          error: null,
          isLoading: false
      }
      
      this.on_add = this.props.on_add;
  }

  handleChange = (field) => (event) => {
    this.setState({
      data: {
        ...this.state.data,
        [field]: field === 'fetch_linked_files' ? event.target.checked : event.target.value
      },
      error: null
    });
  }

  validateForm = () => {
    const { name, url, refresh } = this.state.data;
    if (!name || !url) {
      this.setState({ error: "Please fill in both name and URL" });
      return false;
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      this.setState({ error: "URL must start with http:// or https://" });
      return false;
    }
    if (!refresh || refresh.trim() === '') {
      this.setState({ error: "Please specify a refresh interval (e.g., '10 min' or '30 sec')" });
      return false;
    }
    // Updated pattern to include seconds
    const refreshPattern = /^\d+\s*(sec|second|min|minute|hour|day|week|s|m|h|d|w)s?$/i;
    if (!refreshPattern.test(refresh)) {
      this.setState({ error: "Invalid refresh interval format. Use formats like '30 sec', '10 min', '1 hour', '1 day'" });
      return false;
    }
    return true;
  }

  onSubmit = (e) => {
    e.preventDefault();
    if (!this.validateForm()) {
      return;
    }

    this.setState({ isLoading: true });
    
    post_json('/source/add', this.state.data, (response) => {
      this.setState({ isLoading: false });
      
      if (response.error) {
        this.setState({ error: response.error });
        return;
      }

      // Clear form and error on success
      this.setState({
        error: null,
        data: {
          name: "",
          url: "",
          refresh: "",
          fetch_linked_files: false
        }
      });

      if (this.props.on_add) {
        this.props.on_add();
      }
    });
  }

  render() {
    const { data, error, isLoading } = this.state;
    
    return (
      <>
        <Form onSubmit={this.onSubmit}>
          <div className="row g-2 mb-2">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label className="mb-1 small">Feed Name</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={data.name}
                  onChange={this.handleChange('name')}
                  placeholder="Enter feed name"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="mb-1 small">Feed URL</Form.Label>
                <Form.Control
                  size="sm"
                  type="url"
                  value={data.url}
                  onChange={this.handleChange('url')}
                  placeholder="https://example.com/feed.xml"
                />
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label className="mb-1 small">Refresh</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={data.refresh}
                  onChange={this.handleChange('refresh')}
                  placeholder="10 min or 30 sec"
                />
              </Form.Group>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col">
              <Form.Check
                type="checkbox"
                label="Download linked files"
                checked={data.fetch_linked_files}
                onChange={this.handleChange('fetch_linked_files')}
              />
            </div>
          </div>
          <div className="d-flex flex-column">
            <div className="d-flex justify-content-between align-items-center">
              <div></div>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Adding...' : 'Add Feed'}
              </Button>
            </div>
            {error && (
              <Alert variant="danger" className="mt-2 mb-0">
                {error}
              </Alert>
            )}
          </div>
        </Form>
      </>
    );
  }
}

class SourceView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hidden: true,
      data: null,
      showDeleteModal: false,
      error: null
    }
    
    this.name = props.name;
    this.on_remove = props.on_remove;
    this.on_rename = props.on_rename;
  }

  componentDidMount() {
    this.update();
  }

  update_json(json) {
    if (json.error) {
      this.setState({ error: json.error });
      return;
    }
    // Filter out the message field from the response data
    const { message, ...sourceData } = json;
    this.setState({ data: sourceData, error: null });
  }

  update() {
    get_json(`/source/get/${this.name}`, (json) => {
      this.update_json(json);
    });
  }

  onSubmit = () => {
    const { data } = this.state;
    
    // Validate refresh interval before submitting
    const refreshPattern = /^\d+\s*(sec|second|min|minute|hour|day|week|s|m|h|d|w)s?$/i;
    if (!data.refresh || data.refresh.trim() === '') {
      this.setState({ error: "Please specify a refresh interval (e.g., '10 min' or '30 sec')" });
      return;
    }
    if (!refreshPattern.test(data.refresh)) {
      this.setState({ error: "Invalid refresh interval format. Use formats like '30 sec', '10 min', '1 hour', '1 day'" });
      return;
    }

    this.setState({ error: null }); // Clear error before request
    post_json(`/source/${data.name}/update`, data, (json) => {
      if (json.error) {
        this.setState({ error: json.error });
        return;
      }
      this.update_json(json);
    });
  }

  onSubmitDisable = () => {
    const { data } = this.state;
    const updatedData = {
      ...data,
      enabled: !data.enabled
    };
    this.setState({ error: null }); // Clear error before request
    post_json(`/source/${data.name}/update`, updatedData, (json) => {
      if (json.error) {
        this.setState({ error: json.error });
        return;
      }
      this.update_json(json);
    });
  }

  onSubmitRemove = () => {
    this.setState({ showDeleteModal: true });
  }

  handleConfirmDelete = () => {
    const { data } = this.state;
    delete_json(`/source/${data.name}/remove`, (response) => {
      if (response.error) {
        this.setState({ 
          error: response.error,
          showDeleteModal: false 
        });
        return;
      }
      this.setState({ showDeleteModal: false, error: null });
      this.on_remove();
    });
  }

  handleCancelDelete = () => {
    this.setState({ showDeleteModal: false });
  }

  render() {
    const { data, showDeleteModal, error } = this.state;
    
    if (!data) return null;

    return (
      <>
        <Card className="h-100 card-inner">
          <Card.Header className="d-flex justify-content-between align-items-center py-2">
            <span className="text-truncate me-2" title={data.name}>{data.name}</span>
            <div className="flex-shrink-0">
              <Button variant="danger" size="sm" onClick={this.onSubmitRemove}>
                Remove
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="py-2">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="mb-1 small">Feed URL</Form.Label>
                <Form.Control
                  size="sm"
                  type="url"
                  value={data.url}
                  onChange={(e) => this.setState({ data: { ...data, url: e.target.value }})}
                  placeholder="https://your.feed.url/something.xml"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="mb-1 small">Refresh Interval</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={data.refresh}
                  onChange={(e) => this.setState({ data: { ...data, refresh: e.target.value }})}
                  placeholder="15 min"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Download linked files"
                  checked={data.fetch_linked_files}
                  onChange={(e) => this.setState({ data: { ...data, fetch_linked_files: e.target.checked }})}
                />
              </Form.Group>

              <div className="d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center">
                  <Button
                    variant={data.enabled ? "warning" : "success"}
                    size="sm"
                    onClick={this.onSubmitDisable}
                  >
                    {data.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="primary" size="sm" onClick={this.onSubmit}>
                    Update
                  </Button>
                </div>
                {error && (
                  <Alert variant="danger" className="mt-2 mb-0">
                    {error}
                  </Alert>
                )}
              </div>
            </Form>
          </Card.Body>
        </Card>

        <ConfirmationModal
          show={showDeleteModal}
          title="Delete Source"
          message={`Are you sure you want to delete the source "${data.name}"?`}
          onConfirm={this.handleConfirmDelete}
          onCancel={this.handleCancelDelete}
        />
      </>
    );
  }
}

class Sources extends React.Component {
  constructor() {
    super();
    this.state = {
      sources: [],
      isLoading: true,
      error: null
    }
  }

  update() {
    this.setState({ isLoading: true, error: null });
    get_json('/source/list', (response) => {  
      if (response.error) {
        this.setState({ 
          error: response.error, 
          isLoading: false 
        });
        return;
      }
      
      this.setState({
        sources: response,
        isLoading: false,
        error: null
      });
    });
  }

  componentDidMount() {
    this.update();
  }

  render() {
    const { sources, isLoading, error } = this.state;

    return (
      <div className="container-fluid px-4">
        <h2 className="mb-4">Feed Sources</h2>

        {error && (
            <Alert variant="danger" className="mb-3">
                {error}
            </Alert>
        )}

        <Card className="mb-4 card-primary">
          <Card.Header>
            <h5 className="mb-0">Add New Feed Source</h5>
          </Card.Header>
          <Card.Body>
            <AddSourceView on_add={() => this.update()}/>
          </Card.Body>
        </Card>

        <Card className="mb-4 card-secondary">
          <Card.Header>
            <h5 className="mb-0">Edit Existing Sources</h5>
          </Card.Header>
          <Card.Body className="p-3">
            {isLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {sources.length === 0 ? (
                  <Alert variant="info" className="m-3">
                    No sources available. Add your first feed source above.
                  </Alert>
                ) : (
                  <div className="row g-3">
                    {sources.map((source) => (
                      <div key={source.id} className="col-12 col-lg-6">
                        <SourceView 
                          name={source.name}
                          on_rename={() => this.update()}
                          on_remove={() => this.update()} 
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }
}
export default Sources